/**
 * <s-product-gallery> — main product image slider with thumbnail navigation.
 *
 * Required markup:
 *   [data-gallery-track]               — horizontal scroll-snap track (position: relative)
 *   [data-gallery-slide][data-media-id] — one per media item inside the track
 *   [data-gallery-thumb]               — thumbnail buttons, same order as slides
 *   [data-gallery-prev] / [data-gallery-next] — optional arrow buttons
 *
 * <s-gallery-sync media-id="123"> — rendered inside the product-form partial; when it
 * connects (page load or variant change) it asks the gallery to show that media.
 *
 * <s-sticky-atc> — mobile sticky add-to-cart bar. Shows when [data-main-atc] leaves
 * the viewport.
 */
const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class SProductGallery extends HTMLElement {
  #ac;
  #observer;
  #index = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.track = this.querySelector('[data-gallery-track]');
    this.slides = [...this.querySelectorAll('[data-gallery-slide]')];
    this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
    if (!this.track || this.slides.length === 0) return;

    this.thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => this.go(i), opts));
    this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.go(this.#index - 1), opts);
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.go(this.#index + 1), opts);

    document.addEventListener(
      'gallery:select',
      (event) => {
        const i = this.slides.findIndex((s) => s.dataset.mediaId === String(event.detail?.mediaId));
        if (i >= 0) this.go(i, !event.detail?.initial);
      },
      opts
    );

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.#setActive(this.slides.indexOf(entry.target));
        }
      },
      { root: this.track, threshold: 0.6 }
    );
    this.slides.forEach((slide) => this.#observer.observe(slide));
    this.#setActive(0);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }

  go(index, smooth = true) {
    const i = Math.max(0, Math.min(this.slides.length - 1, index));
    const slide = this.slides[i];
    this.track.scrollTo({ left: slide.offsetLeft, behavior: smooth && !reduceMotion() ? 'smooth' : 'auto' });
    this.#setActive(i);
  }

  #setActive(index) {
    if (index < 0) return;
    this.#index = index;
    this.thumbs.forEach((thumb, i) => {
      const active = i === index;
      thumb.toggleAttribute('data-active', active);
      if (active) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
    const prev = this.querySelector('[data-gallery-prev]');
    const next = this.querySelector('[data-gallery-next]');
    if (prev) prev.disabled = index === 0;
    if (next) next.disabled = index === this.slides.length - 1;
  }
}

class SGallerySync extends HTMLElement {
  static #initial = true;

  connectedCallback() {
    const mediaId = this.getAttribute('media-id');
    if (!mediaId) return;
    const initial = SGallerySync.#initial;
    SGallerySync.#initial = false;
    requestAnimationFrame(() => {
      document.dispatchEvent(new CustomEvent('gallery:select', { detail: { mediaId, initial } }));
    });
  }
}

class SStickyAtc extends HTMLElement {
  #observer;

  connectedCallback() {
    const target = document.querySelector('[data-main-atc]');
    if (!target) return;
    // Clicking the bar presses the real add button so the current variant and quantity are used;
    // when the variant is sold out there is no add button, so bring the form into view instead.
    this.querySelector('[data-sticky-trigger]')?.addEventListener('click', () => {
      const addButton = target.querySelector('button[name="add"]');
      if (addButton) addButton.click();
      else target.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'center' });
    });
    this.#observer = new IntersectionObserver(([entry]) => {
      const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      this.toggleAttribute('data-visible', show);
      this.inert = !show;
    });
    this.inert = true;
    this.#observer.observe(target);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }
}

if (!customElements.get('s-product-gallery')) customElements.define('s-product-gallery', SProductGallery);
if (!customElements.get('s-gallery-sync')) customElements.define('s-gallery-sync', SGallerySync);
if (!customElements.get('s-sticky-atc')) customElements.define('s-sticky-atc', SStickyAtc);
